import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ManageSearch as ManageSearchIcon } from '@frontierag/frontier-ui-library/icons';
import {
  APP_PRODUCT_TYPE,
  GROUPED_PRODUCTS_TYPES,
  LOAD_PRODUCTS_LIMIT,
  MAX_NUMBER_OF_PRODUCTS_PER_SUBGROUP,
} from 'appConstants';
import { GroupedProductSelect } from 'components';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useProducts } from 'contexts/Products';
import type { IGroupedProduct } from 'contexts/Products/types';
import isEqual from 'lodash/isEqual';

import type { IFertProductSelectProps } from './types';

const getOptionLabel = (option: IGroupedProduct | string): string =>
  typeof option === 'object' ? `${option.itemDescription}` : option;

export default function FertProductSelect({
  disabled,
  location,
  selectedProductReference,
  textFieldProps,
  limitResult = MAX_NUMBER_OF_PRODUCTS_PER_SUBGROUP,
  subGroup,
  onChange = () => {},
}: IFertProductSelectProps) {
  const { t } = useTranslation('FertProductSelect');

  const {
    state,
    actions: { loadProducts },
    selectors: { selectGroupedProducts },
  } = useProducts();
  const indexedProducts = selectGroupedProducts(state);
  const { loading, loadedProductType, error, loadedParams } = state;

  const products = Object.values(indexedProducts); // Remove duplicates

  const currentSelectedProduct = indexedProducts[selectedProductReference] || null;

  const filteredProducts = useMemo(() => {
    return [
      ...(subGroup
        ? products.filter(
            (currentProduct) =>
              currentProduct.subGroups.includes(subGroup) || currentProduct.isRecent,
          )
        : products),
    ].sort(
      (a, b) =>
        b.isRecent || a.isRecent
          ? +b.isRecent - +a.isRecent
          : -getOptionLabel(b).localeCompare(getOptionLabel(a)), // Sort products by recent or label
    );
  }, [products, subGroup]);

  useEffect(() => {
    const params = { limit: LOAD_PRODUCTS_LIMIT, location };
    if (
      location &&
      (loadedProductType !== APP_PRODUCT_TYPE.FERTILISER || !isEqual(params, loadedParams)) &&
      !loading &&
      !error
    ) {
      loadProducts(params, APP_PRODUCT_TYPE.FERTILISER);
    }
  }, [error, loadProducts, loadedParams, loadedProductType, loading, location]);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <DynamicAutocomplete
      disabled={disabled}
      textFieldProps={{
        label: t('labels.product'),
        placeholder: t('placeholders.searchForProduct'),
        ...textFieldProps,
      }}
      startAdornmentIcon={<ManageSearchIcon />}
      noOptionsText={t('labels.noOptions')}
      value={currentSelectedProduct}
      options={filteredProducts}
      getOptionLabel={(option) => getOptionLabel(option)}
      onChange={handleOnChange}
      loading={loading}
      groupBy={(option) => (option.isRecent ? 'recent' : 'limited')}
      displayPopupIcon
      filterOptionsProps={{
        limit: limitResult,
        stringify: (option) => getOptionLabel(option),
      }}
      renderGroup={(params) => (
        <GroupedProductSelect
          key={params.key}
          group={
            params.group === 'recent'
              ? GROUPED_PRODUCTS_TYPES.LAST_USED
              : GROUPED_PRODUCTS_TYPES.LIMITED
          }
          transNamespace="FertProductSelect"
        >
          {params.children}
        </GroupedProductSelect>
      )}
      isOptionEqualToValue={(option, value) => option.itemGroupRef === value.itemGroupRef}
    />
  );
}
